import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Cabecalho from '../cabecalho/Cabecalho';
import Rodape from '../rodape/Rodape';
import Menu from '../menu/Menu';
import Titulo from '../titulo/Titulo';
import ComunicadosService from '../../service/comunicados/ComunicadosService';

// =============================================================================
// PAGINA: RESULTADOS DO COMUNICADO
// =============================================================================
// Mostra:
//   - Resumo (total respostas, taxa)
//   - Grafico de pizza (multipla escolha) - barras horizontais
//   - Lista de respostas (texto livre)
// =============================================================================

const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: #fff;
`;

const MainContent = styled.main`
    padding-top: 85px;
    padding-left: 260px;
    padding-right: 24px;
    padding-bottom: 60px;
    box-sizing: border-box;
    flex: 1 0 auto;
    @media (max-width: 1200px) and (min-width: 768px) {
        padding-left: 80px;
    }
    @media (max-width: 767px) {
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: 16px;
    }
`;

const Container = styled.div`
    padding: 24px;
    max-width: 900px;
    margin: 0 auto;
    font-family: 'Roboto', sans-serif;
`;

const InfoCard = styled.div`
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 24px;
    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const StatCard = styled.div`
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    text-align: center;
`;

const StatValue = styled.div`
    font-size: 28px;
    font-weight: bold;
    color: #008080;
    margin: 8px 0 4px 0;
`;

const StatLabel = styled.div`
    font-size: 13px;
    color: #777;
    text-transform: uppercase;
`;

const BarraContainer = styled.div`
    margin: 12px 0;
`;

const BarraLabel = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    margin-bottom: 4px;
    color: #333;
`;

const BarraFundo = styled.div`
    background: #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    height: 24px;
`;

const BarraProgresso = styled.div`
    background: ${(props) => props.$cor || '#008080'};
    height: 100%;
    width: ${(props) => props.$percent}%;
    transition: width 0.4s ease;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 8px;
    color: white;
    font-size: 12px;
    font-weight: bold;
`;

const ListaRespostas = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 500px;
    overflow-y: auto;
`;

const RespostaItem = styled.div`
    padding: 12px;
    background: #f8f9fa;
    border-radius: 6px;
    font-size: 14px;
    color: #333;
`;

const Badge = styled.span`
    display: inline-block;
    background-color: ${(props) => {
        if (props.$tipo === 'informativo') return '#0288d1';
        if (props.$tipo === 'sim_nao') return '#7b1fa2';
        if (props.$tipo === 'multipla_escolha') return '#f57c00';
        if (props.$tipo === 'texto_livre') return '#388e3c';
        return '#757575';
    }};
    color: #fff;
    font-size: 11px;
    font-weight: bold;
    padding: 3px 8px;
    border-radius: 12px;
    text-transform: uppercase;
    margin-right: 8px;
`;

const Loading = styled.div`
    text-align: center;
    padding: 60px 20px;
    color: #008080;
`;

const VoltarButton = styled.button`
    background: transparent;
    border: 1px solid #008080;
    color: #008080;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    margin-bottom: 16px;
    &:hover { background: #f0fdfd; }
`;

const EmptyText = styled.p`
    color: #999;
    text-align: center;
    padding: 30px;
    font-style: italic;
`;

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

const ResultadosComunicado = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [comunicado, setComunicado] = useState(null);
    const [respostas, setRespostas] = useState([]);
    const [estatisticas, setEstatisticas] = useState({ total: 0, contagem: {} });
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        carregar();
    }, [id]);

    const carregar = async () => {
        setLoading(true);
        setErro(null);
        try {
            const [c, r, e] = await Promise.all([
                ComunicadosService.obter(id),
                ComunicadosService.listarRespostas(id),
                ComunicadosService.obterEstatisticas(id),
            ]);
            setComunicado(c);
            setRespostas(Array.isArray(r) ? r : []);
            setEstatisticas(e || { total: 0, contagem: {} });
        } catch (err) {
            console.error('Erro ao carregar resultados:', err);
            setErro('Erro ao carregar resultados.');
        } finally {
            setLoading(false);
        }
    };

    const formatarData = (iso) => {
        if (!iso) return '-';
        try {
            return new Date(iso).toLocaleString('pt-BR');
        } catch {
            return iso;
        }
    };

    const labelTipo = (tipo) => {
        switch (tipo) {
            case 'informativo': return 'INFORMATIVO';
            case 'sim_nao': return 'SIM/NÃO';
            case 'multipla_escolha': return 'ENQUETE';
            case 'texto_livre': return 'TEXTO LIVRE';
            default: return (tipo || '').toUpperCase();
        }
    };

    // Calcula percentuais pra grafico de barras
    const renderGrafico = () => {
        const contagem = estatisticas.contagem || {};
        const total = estatisticas.total || 0;
        const entries = Object.entries(contagem).sort((a, b) => b[1] - a[1]);

        if (entries.length === 0) {
            return <EmptyText>Sem respostas ainda.</EmptyText>;
        }

        const cores = ['#008080', '#f57c00', '#0288d1', '#7b1fa2', '#388e3c', '#d32f2f', '#fbc02d', '#5d4037'];

        return entries.map(([opcao, qtd], idx) => {
            const percent = total > 0 ? Math.round((qtd / total) * 100) : 0;
            return (
                <BarraContainer key={opcao}>
                    <BarraLabel>
                        <span><strong>{opcao}</strong></span>
                        <span>{qtd} respostas ({percent}%)</span>
                    </BarraLabel>
                    <BarraFundo>
                        <BarraProgresso $percent={percent} $cor={cores[idx % cores.length]}>
                            {percent >= 10 ? `${percent}%` : ''}
                        </BarraProgresso>
                    </BarraFundo>
                </BarraContainer>
            );
        });
    };

    const renderListaRespostas = () => {
        if (respostas.length === 0) {
            return <EmptyText>Sem respostas ainda.</EmptyText>;
        }

        return (
            <ListaRespostas>
                {respostas.map((r, idx) => {
                    const texto = r.resposta?.texto || r.resposta?.opcao || JSON.stringify(r.resposta);
                    const nome = r.usuarioId?.nome || 'Usuário';
                    return (
                        <RespostaItem key={r._id || idx}>
                            <strong>{nome}</strong>: {texto}
                            <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                                {formatarData(r.createdAt)}
                            </div>
                        </RespostaItem>
                    );
                })}
            </ListaRespostas>
        );
    };

    if (loading) {
        return (
            <PageWrapper>
                <Menu />
                <Cabecalho />
                <MainContent>
                    <Container>
                        <Loading>Carregando resultados...</Loading>
                    </Container>
                </MainContent>
            </PageWrapper>
        );
    }

    if (erro) {
        return (
            <PageWrapper>
                <Menu />
                <Cabecalho />
                <MainContent>
                    <Container>
                        <VoltarButton onClick={() => navigate('/comunicacoes-especiais')}>← Voltar</VoltarButton>
                        <div style={{ background: '#fde8e8', color: '#c62828', padding: 16, borderRadius: 6 }}>{erro}</div>
                    </Container>
                </MainContent>
            </PageWrapper>
        );
    }

    const taxa = comunicado?.totalEnvios > 0
        ? Math.round((estatisticas.total / comunicado.totalEnvios) * 100)
        : 0;

    return (
        <PageWrapper>
            <Menu />
            <Cabecalho />
            <MainContent>
                <Container>
                    <VoltarButton onClick={() => navigate('/comunicacoes-especiais')}>← Voltar para Comunicados</VoltarButton>
                    <Titulo>Resultados do Comunicado</Titulo>

                    {comunicado && (
                        <InfoCard>
                            <div>
                                <Badge $tipo={comunicado.tipoPopup}>{labelTipo(comunicado.tipoPopup)}</Badge>
                                {comunicado.obrigatorio && (
                                    <span style={{
                                        display: 'inline-block',
                                        background: '#d32f2f',
                                        color: 'white',
                                        padding: '3px 8px',
                                        borderRadius: 12,
                                        fontSize: 11,
                                        fontWeight: 'bold'
                                    }}>OBRIGATORIO</span>
                                )}
                            </div>
                            <h2 style={{ margin: '12px 0 4px 0', color: '#333' }}>{comunicado.titulo}</h2>
                            <p style={{ margin: 0, color: '#555' }}>{comunicado.mensagem}</p>
                            <p style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                                Criado em: {formatarData(comunicado.createdAt)}
                            </p>
                        </InfoCard>
                    )}

                    <StatsGrid>
                        <StatCard>
                            <StatLabel>Enviado para</StatLabel>
                            <StatValue>{comunicado?.totalEnvios || 0}</StatValue>
                            <StatLabel>dispositivos</StatLabel>
                        </StatCard>
                        <StatCard>
                            <StatLabel>Receberam</StatLabel>
                            <StatValue>{estatisticas.total}</StatValue>
                            <StatLabel>respostas</StatLabel>
                        </StatCard>
                        <StatCard>
                            <StatLabel>Taxa</StatLabel>
                            <StatValue>{taxa}%</StatValue>
                            <StatLabel>de resposta</StatLabel>
                        </StatCard>
                    </StatsGrid>

                    <InfoCard>
                        <h3 style={{ marginTop: 0, color: '#008080' }}>
                            📊 Distribuição das respostas
                        </h3>
                        {renderGrafico()}
                    </InfoCard>

                    {(comunicado?.tipoPopup === 'texto_livre' || respostas.length > 0) && (
                        <InfoCard>
                            <h3 style={{ marginTop: 0, color: '#008080' }}>
                                💬 Respostas individuais ({respostas.length})
                            </h3>
                            {renderListaRespostas()}
                        </InfoCard>
                    )}
                </Container>
            </MainContent>
            <Rodape />
        </PageWrapper>
    );
};

export default ResultadosComunicado;
